import { ChangeState, FSM, State } from 'afsm';
import {
  IEncodeFrame,
  IRemoteTrack,
  IRoom,
  PublishedUser,
  RemoteStreamType,
  RemoteTrackEvent,
  RemoteTrackEventTypes,
  TrackEvent,
  VideoCodec
} from '../enums';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT from '../inner-emitter/inner-event';
import { MediaTypes, Track, TrackType } from './track';
import { CheckPendingPromiseType, checkPendingPromise } from '../utils/decorators/check-pending-promise';
import { NAME } from '../common/constants';
import kvStatManager, { KV_REPORT_KEY_AUDIO_DECODE, KV_REPORT_KEY_VIDEO_DECODE, KV_REPORT_KEY_NETWORK } from '../manager/kv-stat-manager';
import { getDeviceModel, getOSNumber, getOSString } from '../utils/environment';
import eventLog from '../utils/log/event-log';
import { reportSuccessRate } from '../utils/decorators/report-success-rate';
import { PlayerEvent } from '../common/event';
import { fromEvent, map, merge, pipe, subscribe, switchMapTo, race, filter, mapTo, timer, subject, takeUntil } from 'fastrx';

export abstract class RemoteTrack<T extends AudioOrVideo> extends Track<T, RemoteTrackEventTypes | { [FSM.INIT]: [any], [RemoteTrack.STATE_SUBSCRIBE]: [any]; }> implements IRemoteTrack<T> {
  tinyId: string;
  isRemote = true;
  jitterBufferDelay = 0;
  availableState: FSM;
  remotePublishState: FSM;
  static readonly STATE_SUBSCRIBE = 'subscribe';
  abstract stat: RemoteAudioStat | RemoteVideoStat;
  private _triggerCheckDecodeSubject = subject(fromEvent(this, RemoteTrack.STATE_SUBSCRIBE));
  ignoreUpdatePlayingState?: boolean;
  constructor(
    public room: IRoom,
    public user: PublishedUser,
    mediaType: MediaTypes<T>
  ) {
    super({ userId: user.userId, sdkAppId: room.sdkAppId, mediaType, room });
    this.tinyId = user.tinyId;
    this.availableState = new FSM(`${user.userId}-${this.mediaType}-available`, 'remote-track-available');
    this.remotePublishState = new FSM(`${user.userId}-${this.mediaType}-remote-publish`, 'remote-track-publish');
    // 将订阅状态和远端 Flag 状态变更事件转换成是否可播放的状态
    pipe(merge(fromEvent(this, FSM.STATECHANGED), fromEvent(this.remotePublishState, FSM.STATECHANGED)), map(() => this.isRemotePublished && (this.isSubscribed || this.isSubscribing)), subscribe((available: boolean) => {
      if (this.availableState.state !== (available ? FSM.ON : FSM.OFF)) this.availableState.state = available ? FSM.ON : FSM.OFF;
      if (this.isRemotePublished && this.ignoreUpdatePlayingState) return;
      this.updatePlayingState(available);
    }));
    // 监听远端解码错误
    const code3Error = pipe(fromEvent(this.player, PlayerEvent.ERROR), filter((error: MediaError) => error.code === MediaError.MEDIA_ERR_DECODE));
    // 监听 5s 后远端解码成功或者失败
    const checkAfter5s = pipe(timer(5000), filter(() => {
      if (this.ignoreDecodeError || !this.isSubscribed || !this.isPlayCalled || !this.stat.bytesReceived || !this.isRemotePublished) return false;
      const success = this.player.isPlaying || (this.kind === NAME.AUDIO ?// Safari 和 Firefox 可能拿不到 framesDecoded，则用 isPlaying 状态来判断
        this.getAudioLevel() > 0 :
        (this.stat as RemoteVideoStat).framesDecoded > 0);
      if (success) {
        this.reportDecodeResult(true);
        return false;
      }
      return true;
    }));
    // 真正触发解码失败
    const realTrigger = pipe(race(code3Error, checkAfter5s), takeUntil(fromEvent(this, FSM.INIT)));
    // 触发判断解码失败/成功逻辑
    pipe(this._triggerCheckDecodeSubject, filter(() => !this.ignoreDecodeError), switchMapTo(realTrigger), subscribe((error: number | Error) => {
      this.reportDecodeResult(false, error);
    }));
  }

  abstract get isRemotePublished(): boolean;
  abstract decodeFrame(frame: IEncodeFrame<T>): IEncodeFrame<T> | undefined;
  setMute(value: boolean) {
    if (this.isRemotePublished) {
      super.setMute(value);
    }
  }
  setInputMediaStreamTrack(mediaTrack: MediaStreamTrack): void {
    super.setInputMediaStreamTrack(mediaTrack as TrackType<T>);
    // remote track 需根据是否有相应的 flag 来给 player 设置 track
    // 否则可能出现远端只推主流，拉流端日志会打印 auxiliary video track muted 的日志。
    if (this.isRemotePublished && this.isSubscribed) {
      this.player.setTrack(this.outMediaTrack);
    }
  }
  checkDecodeResult() {
    this._triggerCheckDecodeSubject.next(true);
  }
  @checkPendingPromise(5, CheckPendingPromiseType.RESOLVE)
  waitHasMediaTrack(): Promise<void> {
    return new Promise(resolve => {
      if (this.mediaTrack) {
        resolve();
      } else {
        this.once(TrackEvent.INPUT_MEDIA_TRACK_CHANGED, resolve);
      }
    });
  }
  get ignoreDecodeError() {
    // 1. 近期出现过差网络（downlinkNetworkQuality > 2），可能会影响解码成功率判断，此时忽略
    // 2. 自动播放失败，不一定是解码失败，此时忽略
    const hadBadNetwork = this.room?.networkQuality?.hadRecentBadDownlink?.(2) ?? false;
    return hadBadNetwork || this.player.isInAutoPlayFailedState;
  }
  get isSubscribing() {
    return this.state.toString() === 'subscribeing';
  }

  get isSubscribed() {
    return this.state === RemoteTrack.STATE_SUBSCRIBE;
  }

  // 远端是否推流 && 是否订阅远端流
  get isAvailable() {
    return this.availableState.state === FSM.ON;
  }

  get isNeedPlay() {
    return this.isAvailable && this.isPlayCalled;
  }

  @ChangeState(FSM.INIT, RemoteTrack.STATE_SUBSCRIBE, {
    success(this: RemoteTrack<T>) {
      this.log.info('subscribed');
      innerEmitter.emit(INNER_EVENT.REMOTE_TRACK_SUBSCRIBED, { track: this });
    },
    ignoreError: true
  })
  @reportSuccessRate(KV_REPORT_KEY_NETWORK.SUBSCRIBE, false)
  subscribe(promise: Promise<any>) {
    return promise;
  }

  @ChangeState(RemoteTrack.STATE_SUBSCRIBE, FSM.INIT, {
    sync: true,
    success(this: RemoteTrack<T>) {
      this.log.info('unsubscribed');
      innerEmitter.emit(INNER_EVENT.REMOTE_TRACK_UNSUBSCRIBED, { track: this });
    }
  })
  unsubscribe() {
    if (this.streamType === RemoteStreamType.Main && this.kind === 'video') {
      // 取消订阅后，重置 small 记录。避免后续重新 subscribe 时，预期拉大流，却拉了小流。
      this.room.changeType(false, this.user);
    }
  }

  reportDecodeResult<T extends boolean>(isDecodeSuccess: T, error?: T extends true ? never : Error | number | undefined) {
    const isAudio = this.kind === NAME.AUDIO;
    kvStatManager[isDecodeSuccess ? 'addSuccessEvent' : 'addFailedEvent']({
      key: isAudio ? KV_REPORT_KEY_AUDIO_DECODE.DECODE_RESULT : KV_REPORT_KEY_VIDEO_DECODE.DECODE_RESULT
    });
    if (!isAudio) {
      const codec = this.room?.downlinkVideoCodec.toUpperCase() || 'H264';
      kvStatManager[isDecodeSuccess ? 'addSuccessEvent' : 'addFailedEvent']({
        key: KV_REPORT_KEY_VIDEO_DECODE[`DECODE_${codec}_RESULT`]
      });
      if (!isDecodeSuccess) {
        this.log.warn(`${this.room?.downlinkVideoCodec} decode failed`);
      }
    }
    if (!isDecodeSuccess) {
      kvStatManager.addEnum({ key: isAudio ? KV_REPORT_KEY_AUDIO_DECODE.DECODE_FAILED_OS : KV_REPORT_KEY_VIDEO_DECODE.DECODE_FAILED_OS, value: getOSNumber() });
      eventLog.uploadEvent({ log: `stat-decode-failed-${this.kind}-${getDeviceModel() || getOSString()}`, userId: this.room.userId });
      this._log.warn(`decode failed: isPlaying: ${this.player.isPlaying} ${this.kind === NAME.AUDIO ? `audioLevel: ${this.getAudioLevel()}` : `framesDecoded: ${(this.stat as RemoteVideoStat).framesDecoded > 0}`}`);
      this.emit(RemoteTrackEvent.DECODE_FAILED, { error });
    }
  }
  updatePlayingState(isNeedPlay: boolean) {
    if (this.player.isPlayCalled) this.player.setTrack(this.playerMediaTrack);
    if (this.isPlayCalled && this.player.isStopped === isNeedPlay) {
      if (isNeedPlay && (!this.isSubscribed || !this.isRemotePublished || !this.outMediaTrack)) {
        this.log.info(`abort play, isSubscribed: ${this.isSubscribed} isAvailable: ${this.isRemotePublished} hasTrack: ${!!this.outMediaTrack} `);
        return;
      }
      super.updatePlayingState(isNeedPlay);
    }
  }
  close(): void {
    super.close();
    if (this.outMediaTrack) {
      this.uninstallTrackEvent(this.outMediaTrack);
    }
  }
  onFlagChanged() {
    this.remotePublishState.state = this.isRemotePublished ? FSM.ON : FSM.OFF;
    this.emit(RemoteTrackEvent.REMOTE_PUBLISH_CHANGED, this.isRemotePublished);
  }

  onTrackMuted() {
    if (this.isNeedPlay) super.onTrackMuted();
  }

  onTrackUnmuted() {
    if (this.isNeedPlay) super.onTrackUnmuted();
  }
  onTrackEnded() {
    if (this.isNeedPlay) super.onTrackEnded();
  }
}
